import { formatCurrency } from './decimal';

/**
 * Payday funding rule summary strings (v4 board, #1377). Pure helpers shared
 * by the collapsed rule rows on the Payday funding page. Amounts render
 * compact: whole-dollar values drop the cents ("$2,700"), fractional values
 * keep them ("$65.50").
 */

/** "$4,150.00" -> "$4,150"; fractional and parenthesized values untouched
 *  except for the same trailing-cents trim. */
export function formatMoneyCompact(value) {
  return formatCurrency(value).replace(/\.00(?=\)?$)/, '');
}

/** 1 -> "1st", 15 -> "15th", 22 -> "22nd"; empty string for non-positive input. */
export function ordinalDay(day) {
  const n = Number(day);
  if (!Number.isFinite(n) || n <= 0) return '';
  const mod10 = n % 10;
  const mod100 = n % 100;
  const suffix =
    mod100 >= 11 && mod100 <= 13 ? 'th'
      : mod10 === 1 ? 'st'
        : mod10 === 2 ? 'nd'
          : mod10 === 3 ? 'rd'
            : 'th';
  return `${n}${suffix}`;
}

/**
 * One-line rule description for a collapsed row.
 * Shapes: "No funding" / "True-up only" / "Uniform $100 · True-up" /
 * "Split $2,700 / $1,450[ / $200][ · True-up]". A MONTHLY true-up cadence
 * (#2081) spells the true-up word as "Monthly true-up"; releasing unspent
 * (#2103) appends " + release" to it.
 *
 * @param {{fundAmountEnabled: boolean, trueUpEnabled: boolean,
 *          fundingMode: string, trueUpCadence?: string,
 *          releaseUnspentEnabled?: boolean, amountA: string,
 *          amountB: string, amountC: string}} rule  UI-shape rule (dollar-string amounts)
 */
export function ruleSummary(rule) {
  if (!rule.fundAmountEnabled && !rule.trueUpEnabled) return 'No funding';
  const trueUp = (rule.trueUpCadence === 'MONTHLY' ? 'Monthly true-up' : 'True-up')
    + (rule.releaseUnspentEnabled ? ' + release' : '');
  if (!rule.fundAmountEnabled) return `${trueUp} only`;
  const a = parseFloat(rule.amountA) || 0;
  let base;
  if (rule.fundingMode === 'SPLIT') {
    const b = parseFloat(rule.amountB) || 0;
    const c = parseFloat(rule.amountC) || 0;
    base = `Split ${formatMoneyCompact(a)} / ${formatMoneyCompact(b)}`;
    if (c > 0) base += ` / ${formatMoneyCompact(c)}`;
  } else {
    base = `Uniform ${formatMoneyCompact(a)}`;
  }
  return rule.trueUpEnabled ? `${base} · ${trueUp}` : base;
}
